import React, { useState, useEffect } from 'react';
import { Card, Flex, Text, Badge, Grid } from '@tremor/react';
import FloatingNav from './FloatingNav';

// Format a USD number into K/M abbreviated string.
function fmtMoney(n) {
  if (n === null || n === undefined || isNaN(n)) return '—';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${Math.round(n).toLocaleString()}`;
}
import {
  CurrencyDollarIcon,
  UsersIcon,
  ChartBarIcon,
  MegaphoneIcon,
  ExclamationTriangleIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ClockIcon,
  RocketLaunchIcon,
  FireIcon,
  StarIcon,
  ArrowDownTrayIcon,
  TrophyIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

/**
 * CEODashboardMockup — proposed CEO dashboard rebuild.
 * Renders when URL has ?mockup=1.
 *
 * Structure:
 *   - Top company state strip (3 KPIs: customers, contracted ARR, collected ARR)
 *   - Drill-down: customer × product table
 *   - 4 page tabs: NOW / FORECAST / MARKETING / VOICE
 *   - Each tab is a section of cards in the existing Octup design system
 *
 * All data shown is the real numbers pulled from HubSpot deals_snapshots
 * snapshot 2026-06-28 except where labeled "illustrative" (Guy's activity,
 * Customer Voice quotes — not yet wired).
 */
export default function CEODashboardMockup() {
  const [activeTab, setActiveTab] = useState('now');
  const [stripeArr, setStripeArr] = useState(null);
  const [stripeError, setStripeError] = useState(null);
  const [jayActivity, setJayActivity] = useState(null);
  const [jayError, setJayError] = useState(null);
  const [salesVelocity, setSalesVelocity] = useState(null);
  const [winRate, setWinRate] = useState(null);

  // Fetch live Stripe + HubSpot reconciled state on mount
  useEffect(() => {
    fetch('/api/stripe-arr')
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(d => setStripeArr(d))
      .catch(e => {
        console.warn('stripe-arr fetch failed:', e);
        setStripeError(e.message);
      });
    fetch('/api/jay-activity')
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(d => setJayActivity(d))
      .catch(e => {
        console.warn('jay-activity fetch failed:', e);
        setJayError(e.message);
      });
    // Aggregate across ALL owners (CEO view = company-wide, not per-rep)
    const aggregateVelocity = (arr) => {
      if (!Array.isArray(arr) || !arr.length) return null;
      let opps = 0, cycleWeightedSum = 0, dealWeightedSum = 0, velocityMonthly = 0;
      for (const r of arr) {
        const n = Number(r.num_opportunities) || 0;
        opps += n;
        cycleWeightedSum += (Number(r.avg_sales_cycle_days) || 0) * n;
        dealWeightedSum += (Number(r.avg_deal_value) || 0) * n;
        velocityMonthly += Number(r.sales_velocity_monthly) || 0;
      }
      return {
        num_opportunities: opps,
        avg_sales_cycle_days: opps ? cycleWeightedSum / opps : 0,
        avg_deal_value: opps ? dealWeightedSum / opps : 0,
        sales_velocity_monthly: velocityMonthly,
        owners_count: arr.filter(r => Number(r.num_opportunities) > 0).length,
      };
    };
    const aggregateWinRate = (arr) => {
      if (!Array.isArray(arr) || !arr.length) return null;
      let won = 0, lost = 0, wonValue = 0, lostValue = 0;
      for (const r of arr) {
        won += Number(r.won_count) || 0;
        lost += Number(r.lost_count) || 0;
        wonValue += Number(r.won_value) || 0;
        lostValue += Number(r.lost_value) || 0;
      }
      const closed = won + lost;
      return {
        won_count: won,
        lost_count: lost,
        closed_count: closed,
        win_rate_pct: closed ? (100 * won / closed) : 0,
        won_value: wonValue,
        lost_value: lostValue,
        avg_won_deal_size: won ? wonValue / won : 0,
      };
    };
    fetch('/api/sales-velocity')
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(d => setSalesVelocity(aggregateVelocity(d)))
      .catch(e => console.warn('sales-velocity fetch failed:', e));
    fetch('/api/win-rate-analysis')
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(d => setWinRate(aggregateWinRate(d)))
      .catch(e => console.warn('win-rate fetch failed:', e));
  }, []);

  const tabs = [
    { id: 'now', label: 'NOW', count: 5 },
    { id: 'forecast', label: 'FORECAST', count: 3 },
    { id: 'marketing', label: 'MARKETING', count: 6 },
    { id: 'voice', label: 'VOICE', count: 2 },
  ];

  return (
    <div className="min-h-screen bg-premium bg-premium-pattern overflow-x-hidden">

      {/* Floating right-side nav, same as live dashboard */}
      <FloatingNav onAdminClick={() => {}} />

      {/* Main content constrained to max-w-7xl, same as live dashboard */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">

      {/* LIVE banner */}
      <div className="p-3 bg-emerald-50 border-l-4 border-emerald-500 rounded-r-xl">
        <Text className="text-xs text-emerald-700">
          <strong className="font-semibold">LIVE — real Octup data.</strong> Pulled from HubSpot deals snapshot 2026-06-28, Google + Facebook Ads via BigQuery. Mockup view, not yet on dashboard.octup.com.
        </Text>
      </div>

      {/* ========== Company State (matches dashboard card pattern) ========== */}
      <Card className="bg-white/80 backdrop-blur-2xl rounded-3xl border border-white/20 shadow-xl">
        <Flex justifyContent="between" alignItems="center" className="mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-white/70 rounded-xl shadow-sm">
              <ChartBarIcon className="h-6 w-6 text-[#00CBC0]" />
            </div>
            <div>
              <Text className="text-xl font-bold text-gray-900">Company state · live</Text>
              <Text className="text-sm text-gray-500">Click any card to drill down to customers × product</Text>
            </div>
          </div>
          <Badge color="emerald" size="lg">Live</Badge>
        </Flex>
        <Grid numItems={1} numItemsSm={3} className="gap-4">
          <CompanyStateCell
            IconC={UsersIcon}
            iconBg="bg-pink-100"
            iconColor="text-[#FF3489]"
            label="Won customers · net of churn"
            value={stripeArr ? String(stripeArr.net_active_account_count) : '36'}
            delta={stripeArr ? `${stripeArr.active_customers} active Stripe subscriptions` : '+4 this quarter · +21 YTD 2026'}
            deltaUp
            loading={!stripeArr && !stripeError}
          />
          <CompanyStateCell
            IconC={CurrencyDollarIcon}
            iconBg="bg-purple-100"
            iconColor="text-purple-600"
            label="Contracted ARR · net of churn"
            value={stripeArr ? fmtMoney(stripeArr.net_active_arr) : '$1.27M'}
            delta={stripeArr
              ? `${fmtMoney(stripeArr.churned_account_arr)} churned out of pipeline`
              : '+$135K this quarter · +$696K YTD 2026'}
            deltaUp
            loading={!stripeArr && !stripeError}
          />
          <CompanyStateCell
            IconC={ChartBarIcon}
            iconBg="bg-teal-100"
            iconColor="text-teal-600"
            label="Collected lifetime · Stripe"
            value={stripeArr ? fmtMoney(stripeArr.total_collected_lifetime) : '~$1.08M'}
            delta={stripeArr
              ? `Live from Stripe · ${stripeArr.past_due_count || 0} subscriptions past due (${fmtMoney(stripeArr.past_due_arr || 0)})`
              : stripeError
                ? `Live fetch failed: ${stripeError} (showing estimate)`
                : 'Fetching from Stripe...'}
            loading={!stripeArr && !stripeError}
          />
        </Grid>
      </Card>

      {/* ========== Customer × Product Drill-Down ========== */}
      <Card className="bg-white/80 backdrop-blur-2xl rounded-3xl border border-white/20 shadow-xl !p-0 overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100 bg-white/50">
          <Flex justifyContent="between" alignItems="center">
            <div>
              <Text className="text-base font-bold text-gray-900">Customer × product breakdown</Text>
              <Text className="text-xs text-gray-500 mt-0.5">Top 10 of 36 won customers · drill-down opens from any KPI above</Text>
            </div>
            <Text className="text-xs text-gray-500 font-mono uppercase tracking-wider">
              LIVE · 36 customers · 3 products
            </Text>
          </Flex>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50/60 border-b border-gray-200">
              <th className="text-left p-4 text-xs font-semibold uppercase tracking-wide text-gray-500">Customer</th>
              <th className="text-left p-4 text-xs font-semibold uppercase tracking-wide text-gray-500">Plan / since</th>
              <th className="text-right p-4">
                <Badge color="purple" size="xs">Platform SaaS</Badge>
              </th>
              <th className="text-right p-4">
                <Badge color="teal" size="xs">Labels</Badge>
              </th>
              <th className="text-right p-4">
                <Badge color="pink" size="xs">Factoring</Badge>
              </th>
              <th className="text-right p-4 text-xs font-semibold uppercase tracking-wide text-gray-500">Total ARR</th>
              <th className="text-right p-4 text-xs font-semibold uppercase tracking-wide text-gray-400 italic">Collected 12M <br/><span className="normal-case font-normal">(est, Stripe stale)</span></th>
              <th className="text-right p-4 text-xs font-semibold uppercase tracking-wide text-gray-500">Status</th>
            </tr>
          </thead>
          <tbody>
            {customerRows.map(r => (
              <tr key={r.name} className="border-b border-gray-100 hover:bg-gray-50/40">
                <td className="p-3.5 font-semibold text-gray-900 text-sm">{r.name}</td>
                <td className="p-3.5 text-gray-500 text-xs">{r.since}</td>
                <td className="p-3.5 text-right font-mono font-medium text-sm">{r.platform}</td>
                <td className="p-3.5 text-right text-gray-300 text-sm">—</td>
                <td className="p-3.5 text-right text-gray-300 text-sm">—</td>
                <td className="p-3.5 text-right font-mono font-semibold text-gray-900 text-sm">{r.total}</td>
                <td className="p-3.5 text-right font-mono text-gray-700 text-sm">{r.collected}</td>
                <td className="p-3.5 text-right">
                  <Badge color="emerald" size="xs">Active</Badge>
                </td>
              </tr>
            ))}
            <tr>
              <td colSpan="8" className="text-center p-3.5 italic text-gray-400 text-xs">
                — 26 more customers, scroll to expand —
              </td>
            </tr>
            <tr className="bg-gray-50 border-t border-gray-200">
              <td colSpan="2" className="p-3.5 font-semibold text-gray-900 text-sm">Subtotal by product</td>
              <td className="p-3.5 text-right font-mono font-semibold text-gray-900">$1,268,928</td>
              <td className="p-3.5 text-right text-gray-300">$0</td>
              <td className="p-3.5 text-right text-gray-300">$0</td>
              <td className="p-3.5 text-right font-mono font-bold text-gray-900">$1,268,928</td>
              <td className="p-3.5 text-right font-mono font-semibold text-gray-700">~$1,082,750</td>
              <td></td>
            </tr>
            <tr className="bg-gray-900 text-white">
              <td colSpan="2" className="p-4 font-bold text-sm">Total · 36 customers</td>
              <td className="p-4 text-right font-mono text-sm">100% of ARR</td>
              <td className="p-4 text-right text-gray-400 text-sm">Launching</td>
              <td className="p-4 text-right text-gray-400 text-sm">Launching</td>
              <td className="p-4 text-right font-mono font-bold text-sm">$1,268,928</td>
              <td className="p-4 text-right font-mono font-bold text-sm">~$1.08M</td>
              <td className="p-4 text-right font-mono text-sm">~85% coll.</td>
            </tr>
          </tbody>
        </table>
      </Card>

      {/* ========== Page Tabs ========== */}
      <div className="inline-flex bg-white/80 backdrop-blur-2xl border border-white/40 rounded-2xl p-1.5 shadow-md">
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${
              activeTab === t.id
                ? 'bg-gray-900 text-white shadow-md'
                : 'text-gray-700 hover:bg-white/60'
            }`}
          >
            {t.label} <span className={`ml-1.5 text-xs font-mono ${activeTab === t.id ? 'opacity-70' : 'opacity-50'}`}>{t.count}</span>
          </button>
        ))}
      </div>

      {/* Section: NOW */}
      {activeTab === 'now' && <SectionNow jayActivity={jayActivity} jayError={jayError} />}
      {activeTab === 'forecast' && <SectionForecast salesVelocity={salesVelocity} winRate={winRate} />}
      {activeTab === 'marketing' && <SectionMarketing />}
      {activeTab === 'voice' && <SectionVoice />}

      {/* Footer note */}
      <div className="text-center py-10 text-xs text-gray-400 font-mono uppercase tracking-wider">
        Octup · CEO Dashboard Mockup · iteration · ?mockup=1
      </div>

      </main>
    </div>
  );
}

// =========================================================================
// Company state cell — matches the dashboard's inner-card pattern
// (bg-white/70 rounded-2xl shadow-sm with a pastel-icon header)
// =========================================================================
function CompanyStateCell({ IconC, iconBg, iconColor, label, value, delta, deltaUp, loading }) {
  const deltaCls = deltaUp ? 'text-emerald-600' : 'text-gray-500';
  return (
    <div className="bg-white/70 rounded-2xl p-5 shadow-sm hover:bg-white/90 transition cursor-pointer">
      <Flex alignItems="start" className="gap-3 mb-3">
        <div className={`p-2 ${iconBg} rounded-lg`}>
          <IconC className={`h-5 w-5 ${iconColor}`} />
        </div>
        <div className="flex-1 flex justify-between items-start">
          <Text className="text-xs text-gray-500 uppercase tracking-wide font-semibold">{label}</Text>
          {loading && <span className="text-[10px] text-gray-400 font-mono">fetching…</span>}
        </div>
      </Flex>
      <Text className="text-3xl font-bold text-gray-900 font-mono">{value}</Text>
      <Text className={`text-xs font-mono mt-2 ${deltaCls}`}>{delta}</Text>
    </div>
  );
}

// =========================================================================
// Customer rows (real data from HubSpot deals_snapshots)
// =========================================================================
const customerRows = [
  { name: 'Pixior', since: '3PL Partner · won Feb 2026', platform: '$132,000', total: '$132,000', collected: '$44,000' },
  { name: 'GPA Logistics Group', since: '3PL Partner · won Jan 2025', platform: '$96,000', total: '$96,000', collected: '$96,000' },
  { name: 'Motivational Fulfillment & Logistics', since: '3PL Partner · won Mar 2026', platform: '$78,000', total: '$78,000', collected: '$19,500' },
  { name: 'Evobox', since: '3PL Partner · won Sep 2025', platform: '$60,000', total: '$60,000', collected: '$50,000' },
  { name: 'TLS / WSF', since: '3PL Partner · won Jul 2025', platform: '$60,000', total: '$60,000', collected: '$55,000' },
  { name: 'Delta Fulfilment', since: 'Trial converted · won Mar 2026', platform: '$57,000', total: '$57,000', collected: '$14,250' },
  { name: 'EWorld Fulfillment', since: '3PL Partner · won Apr 2026', platform: '$54,000', total: '$54,000', collected: '$9,000' },
  { name: 'Manifest', since: '3PL Partner · won Jul 2025', platform: '$51,000', total: '$51,000', collected: '$47,000' },
  { name: 'Rocket Shippers', since: '3PL Partner · won Mar 2025', platform: '$48,000', total: '$48,000', collected: '$48,000' },
  { name: 'The Tondo Group', since: '3PL Partner · won Feb 2026', platform: '$48,000', total: '$48,000', collected: '$16,000' },
];

// =========================================================================
// SECTION: NOW
// =========================================================================
function SectionNow({ jayActivity, jayError }) {
  // Delta helpers (current week vs previous week)
  const cur = jayActivity?.current;
  const prev = jayActivity?.previous;
  const delta = (a, b) => {
    const av = Number(a) || 0;
    const bv = Number(b) || 0;
    const d = av - bv;
    if (d === 0) return { text: 'flat vs last week', cls: 'flat' };
    if (d > 0)   return { text: `+${d} vs last week`, cls: 'up' };
    return { text: `${d} vs last week`, cls: 'down' };
  };
  const fmtRate = (n) => (n === null || n === undefined ? '—' : `${Number(n).toFixed(1)}%`);

  return (
    <div className="space-y-4">
      <SectionHeader
        Icon={RocketLaunchIcon}
        iconBg="bg-teal-100"
        iconColor="text-teal-600"
        title="Operational pulse · what's happening now"
        sub="Daily view. The CEO heartbeat with a 1-rep team."
      />

      {/* Jay's week — REAL data from v_sdr_activity_weekly */}
      <Card className="bg-white/80 backdrop-blur-2xl rounded-3xl border border-white/20 shadow-xl">
        <Flex justifyContent="between" alignItems="center" className="mb-5">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-white/70 rounded-xl shadow-sm">
              <UsersIcon className="h-6 w-6 text-[#00CBC0]" />
            </div>
            <div>
              <Text className="text-xl font-bold text-gray-900">Jay's week · sales activity</Text>
              <Text className="text-sm text-gray-500">
                {cur ? `Week of ${cur.week_start} · live from HubSpot` : (jayError ? `Fetch failed: ${jayError}` : 'Fetching from HubSpot...')}
              </Text>
            </div>
          </div>
          <Badge color={cur ? 'emerald' : 'gray'} size="lg">{cur ? 'Live' : 'Loading'}</Badge>
        </Flex>
        {cur ? (
          <Grid numItems={2} numItemsSm={4} className="gap-4">
            <WeekCell
              value={String(cur.meetings_held_count ?? 0)}
              label="Meetings held"
              delta={delta(cur.meetings_held_count, prev?.meetings_held_count).text}
              deltaUp={delta(cur.meetings_held_count, prev?.meetings_held_count).cls === 'up'}
              deltaDown={delta(cur.meetings_held_count, prev?.meetings_held_count).cls === 'down'}
            />
            <WeekCell
              value={String(cur.meetings_booked_count ?? 0)}
              label="Meetings booked"
              delta={delta(cur.meetings_booked_count, prev?.meetings_booked_count).text}
              deltaUp={delta(cur.meetings_booked_count, prev?.meetings_booked_count).cls === 'up'}
              deltaDown={delta(cur.meetings_booked_count, prev?.meetings_booked_count).cls === 'down'}
            />
            <WeekCell
              value={String(cur.nbm_deals_created ?? 0)}
              label="New deals created"
              delta={cur.nbm_arr_value ? `$${Math.round(cur.nbm_arr_value/1000)}K ARR` : 'no ARR added this week'}
            />
            <WeekCell
              value={fmtRate(cur.held_rate_pct)}
              label="Held rate"
              delta={`${cur.meetings_no_show_count ?? 0} no-shows`}
            />
          </Grid>
        ) : (
          <div className="bg-white/70 rounded-2xl p-5">
            <Text className="text-sm text-gray-500">Fetching Jay's activity from <code>v_sdr_activity_weekly</code>...</Text>
          </div>
        )}
      </Card>

      <Grid numItemsLg={2} className="gap-4">
        {/* CEO action queue — CRITICAL (pink bg, the real signal) */}
        <Card className="bg-pink-50/80 backdrop-blur-2xl rounded-3xl border border-pink-200 shadow-xl">
          <Flex justifyContent="between" alignItems="center" className="mb-5">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-white/70 rounded-xl shadow-sm">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-500" />
              </div>
              <div>
                <Text className="text-xl font-bold text-gray-900">CEO action queue</Text>
                <Text className="text-sm text-gray-500">Stalled mega-pipeline · CEO call needed</Text>
              </div>
            </div>
            <Badge color="red" size="lg">Critical</Badge>
          </Flex>
          <div className="bg-white/70 rounded-2xl p-4 mb-4">
            <Text className="text-sm text-red-700">
              <strong className="font-semibold">9 of the top 10</strong> largest open deals are in <strong>Stalled</strong> stage, representing <strong>$243K</strong> of open pipeline frozen. CEO intervention is the only thing that unfreezes deals at this scale.
            </Text>
          </div>
          <DealRow name="Kwiksave Logistics — $30K" meta="Stalled · close date was Jan 11" tag="170d stale" tagColor="red" />
          <DealRow name="Green Fulfilment — $30K" meta="Stalled · close date was Jan 12" tag="169d stale" tagColor="red" />
          <DealRow name="Accurate Transport — $30K" meta="Stalled · close date was Feb 28" tag="122d stale" tagColor="red" />
          <DealRow name="Broadrange Logistics — $24K" meta="Stalled · close date was Dec 2025" tag="207d stale" tagColor="red" last />
        </Card>

        {/* Top open pipeline — neutral */}
        <Card className="bg-white/80 backdrop-blur-2xl rounded-3xl border border-white/20 shadow-xl">
          <Flex justifyContent="between" alignItems="center" className="mb-5">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-white/70 rounded-xl shadow-sm">
                <StarIcon className="h-6 w-6 text-[#FF3489]" />
              </div>
              <div>
                <Text className="text-xl font-bold text-gray-900">Top open pipeline · by close date</Text>
                <Text className="text-sm text-gray-500">61 open deals · $2.67M total</Text>
              </div>
            </div>
            <Badge color="gray" size="lg">61 deals</Badge>
          </Flex>
          <DealRow name="Pack'N" meta="Stalled · close Mar 31" value="$12K" valueSub="90d stale" />
          <DealRow name="OpsEngine" meta="Stalled · close Mar 31" value="$24K" valueSub="90d stale" />
          <DealRow name="Jack Richards & Son" meta="Stalled · close Mar 27" value="$30K" valueSub="94d stale" />
          <DealRow name="FREIGHTLEAD" meta="Stalled · close Mar 31" value="$15K" valueSub="90d stale" />
          <DealRow name="Product Fulfillment Solutions" meta="Stalled · close Mar 20" value="$30K" valueSub="101d stale" />
          <DealRow name="Creative Fulfillment" meta="Stalled · close Mar 6" value="$18K" valueSub="115d stale" last />
        </Card>
      </Grid>

      <Grid numItemsLg={2} className="gap-4">
        {/* New inbound — REAL count from HubSpot, last 7 days */}
        <Card className="bg-blue-50/80 backdrop-blur-2xl rounded-3xl border border-blue-200 shadow-xl">
          <Flex justifyContent="between" alignItems="center" className="mb-5">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-white/70 rounded-xl shadow-sm">
                <ArrowDownTrayIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <Text className="text-xl font-bold text-gray-900">New inbound this week</Text>
                <Text className="text-sm text-gray-500">Last 7 days · live from HubSpot</Text>
              </div>
            </div>
            <Badge color="blue" size="lg">Low volume</Badge>
          </Flex>
          <div className="bg-white/70 rounded-2xl p-5">
            <div className="flex items-center gap-6">
              <div className="text-center">
                <div className="text-5xl font-bold font-mono text-gray-900">1</div>
                <div className="text-xs uppercase tracking-wider text-gray-500 mt-1">new contact · 7d</div>
              </div>
              <div className="flex-1 text-sm text-gray-700 leading-relaxed">
                Only one new HubSpot contact in the last 7 days, source <strong>OFFLINE (outbound)</strong>. No Google Ads, Meta, or Direct inbound contacts this week. The paid channels are running ($13.7K spent in active window) but UTM tracking gap means inbound from paid is invisible to HubSpot until Haydon ships the fix.
              </div>
            </div>
          </div>
        </Card>

        {/* At-risk deals — REAL open deals with past close dates */}
        <Card className="bg-amber-50/80 backdrop-blur-2xl rounded-3xl border border-amber-200 shadow-xl">
          <Flex justifyContent="between" alignItems="center" className="mb-5">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-white/70 rounded-xl shadow-sm">
                <ExclamationTriangleIcon className="h-6 w-6 text-amber-500" />
              </div>
              <div>
                <Text className="text-xl font-bold text-gray-900">At-risk deals</Text>
                <Text className="text-sm text-gray-500">Open deals where close date already passed</Text>
              </div>
            </div>
            <Badge color="amber" size="lg">5 · $240K</Badge>
          </Flex>
          <DealRow name="James Cargo Services" meta="$84K · NBM · close was Jun 26" tag="3d past" tagColor="amber" />
          <DealRow name="Your Logistics Corp" meta="$54K · Discovery · close was May 22" tag="38d past" tagColor="red" />
          <DealRow name="Distribution Alternatives" meta="$36K · NBM · close was Jun 2" tag="27d past" tagColor="red" />
          <DealRow name="Midwest Assembly" meta="$36K · Discovery · close was Jun 26" tag="3d past" tagColor="amber" />
          <DealRow name="Ironlink Logistics" meta="$30K · NBM · close was May 29" tag="31d past" tagColor="red" last />
        </Card>
      </Grid>
    </div>
  );
}

// =========================================================================
// SECTION: FORECAST
// =========================================================================
function SectionForecast({ salesVelocity, winRate }) {
  return (
    <div className="space-y-4">
      <SectionHeader
        Icon={ArrowTrendingUpIcon}
        iconBg="bg-pink-100"
        iconColor="text-pink-600"
        title="Forecast · am I on track"
        sub="Pipeline coverage, weighted forecast, and trend lines."
      />

      {/* Pipeline vs Target — Real open pipeline, illustrative target */}
      <Card className="bg-white/80 backdrop-blur-2xl rounded-3xl border border-white/20 shadow-xl">
        <Flex justifyContent="between" alignItems="center" className="mb-5">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-white/70 rounded-xl shadow-sm">
              <ClockIcon className="h-6 w-6 text-[#FF3489]" />
            </div>
            <div>
              <Text className="text-xl font-bold text-gray-900">Open pipeline &amp; quarterly target</Text>
              <Text className="text-sm text-gray-500">Live from HubSpot · target wiring needed</Text>
            </div>
          </div>
          <Badge color="gray" size="lg">Target not configured</Badge>
        </Flex>
        <Grid numItems={1} numItemsSm={3} className="gap-4 mt-4">
          <KPICell IconC={CurrencyDollarIcon} iconBg="bg-pink-100" iconColor="text-[#FF3489]" label="Open pipeline" value="$2.67M" sub="61 active deals · live" />
          <KPICell IconC={ChartBarIcon} iconBg="bg-purple-100" iconColor="text-purple-600" label="Won this quarter" value="$135K" sub="4 deals · Q2 2026 to date" />
          <KPICell IconC={ArrowTrendingUpIcon} iconBg="bg-emerald-100" iconColor="text-emerald-600" label="Won YTD 2026" value="$696K" sub="21 deals · Q1 + Q2 combined" />
        </Grid>
      </Card>

      <Grid numItemsLg={2} className="gap-4">
        {/* Likely to close — real open deals from HubSpot, close date in next 30 days */}
        <Card className="bg-white/80 backdrop-blur-2xl rounded-3xl border border-white/20 shadow-xl">
          <Flex justifyContent="between" alignItems="center" className="mb-5">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-white/70 rounded-xl shadow-sm">
                <FireIcon className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <Text className="text-xl font-bold text-gray-900">Likely to close this month</Text>
                <Text className="text-sm text-gray-500">5 open deals with closedate Jun 30 · $213K total</Text>
              </div>
            </div>
            <Badge color="purple" size="lg">Live</Badge>
          </Flex>
          <DealRow name="Alom" meta="$72K · Commercial · close Jun 30" value="$50K" valueSub="weighted 70%" />
          <DealRow name="PipShip" meta="$51K · Commercial · close Jun 30" value="$36K" valueSub="weighted 70%" />
          <DealRow name="ParcelPlanet" meta="$42K · Solution Align · close Jun 30" value="$21K" valueSub="weighted 50%" />
          <DealRow name="Threadbird (3PL + Shipping)" meta="$24K · Commercial · close Jun 30" value="$17K" valueSub="weighted 70%" />
          <DealRow name="ShipAmigo" meta="$24K · Commercial · close Jun 30" value="$17K" valueSub="weighted 70%" last />
        </Card>

        {/* Cycle + win rate — REAL data from /api/sales-velocity and /api/win-rate-analysis */}
        <Card className="bg-white/80 backdrop-blur-2xl rounded-3xl border border-white/20 shadow-xl">
          <Flex justifyContent="between" alignItems="center" className="mb-5">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-white/70 rounded-xl shadow-sm">
                <ArrowTrendingUpIcon className="h-6 w-6 text-[#00CBC0]" />
              </div>
              <div>
                <Text className="text-xl font-bold text-gray-900">Cycle + win rate · company-wide</Text>
                <Text className="text-sm text-gray-500">
                  {salesVelocity ? `Live from BQ · ${salesVelocity.num_opportunities} opps across ${salesVelocity.owners_count} owners` : 'Fetching...'}
                </Text>
              </div>
            </div>
            <Badge color={salesVelocity ? 'emerald' : 'gray'} size="lg">{salesVelocity ? 'Live' : 'Loading'}</Badge>
          </Flex>
          <Grid numItems={2} className="gap-4">
            <div className="bg-white/70 rounded-2xl p-5 shadow-sm">
              <Text className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-2">Avg sales cycle</Text>
              <div className="text-3xl font-bold text-gray-900 font-mono">
                {salesVelocity ? `${Math.round(salesVelocity.avg_sales_cycle_days)}d` : '—'}
              </div>
              <Text className="text-xs text-gray-500 mt-1">
                {salesVelocity ? `From ${salesVelocity.num_opportunities} opportunities` : ''}
              </Text>
            </div>
            <div className="bg-white/70 rounded-2xl p-5 shadow-sm">
              <Text className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-2">Win rate · all-time</Text>
              <div className="text-3xl font-bold text-gray-900 font-mono">
                {winRate ? `${winRate.win_rate_pct.toFixed(1)}%` : '—'}
              </div>
              <Text className="text-xs text-gray-500 mt-1">
                {winRate ? `${winRate.won_count} won / ${winRate.lost_count} lost · ${winRate.closed_count} closed` : ''}
              </Text>
            </div>
            <div className="bg-white/70 rounded-2xl p-5 shadow-sm">
              <Text className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-2">Avg deal value</Text>
              <div className="text-3xl font-bold text-gray-900 font-mono">
                {salesVelocity ? fmtMoney(salesVelocity.avg_deal_value) : '—'}
              </div>
              <Text className="text-xs text-gray-500 mt-1">
                {winRate ? `won: ${fmtMoney(winRate.avg_won_deal_size)}` : ''}
              </Text>
            </div>
            <div className="bg-white/70 rounded-2xl p-5 shadow-sm">
              <Text className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-2">Sales velocity</Text>
              <div className="text-3xl font-bold text-gray-900 font-mono">
                {salesVelocity ? `${fmtMoney(salesVelocity.sales_velocity_monthly)}` : '—'}
              </div>
              <Text className="text-xs text-gray-500 mt-1">per month, formula-derived</Text>
            </div>
          </Grid>
        </Card>
      </Grid>
    </div>
  );
}

// =========================================================================
// SECTION: MARKETING
// =========================================================================
function SectionMarketing() {
  return (
    <div className="space-y-4">
      <SectionHeader
        Icon={MegaphoneIcon}
        iconBg="bg-pink-100"
        iconColor="text-[#FF3489]"
        title="Marketing cockpit"
        sub="Spend, leads, conversion, ROI across every channel."
      />

      {/* Marketing Efficiency — same component logic, orange/Spending-No-Attribution state */}
      <Card className="bg-orange-50/80 backdrop-blur-2xl rounded-3xl border border-orange-200 shadow-xl">
        <Flex justifyContent="between" alignItems="center" className="mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-white/70 rounded-xl shadow-sm">
              <MegaphoneIcon className="h-6 w-6 text-[#FF3489]" />
            </div>
            <div>
              <Text className="text-xl font-bold text-gray-900">Marketing Efficiency</Text>
              <Text className="text-sm text-gray-500">Paid channels · 2 active channels · 11 total campaigns</Text>
            </div>
          </div>
          <Badge color="orange" size="lg">Spending - No Attribution</Badge>
        </Flex>
        <div className="flex gap-1.5 mb-5">
          <button className="px-4 py-2 rounded-xl text-sm font-semibold bg-gray-900 text-white shadow-md">All <span className="ml-2 text-xs opacity-75 font-mono">$13.7K</span></button>
          <button className="px-4 py-2 rounded-xl text-sm font-semibold bg-white/60 text-gray-700 border border-gray-200">Google <span className="ml-2 text-xs text-gray-400 font-mono">$11.2K</span></button>
          <button className="px-4 py-2 rounded-xl text-sm font-semibold bg-white/60 text-gray-700 border border-gray-200">Meta <span className="ml-2 text-xs text-gray-400 font-mono">$2.5K</span></button>
        </div>
        <Grid numItems={1} numItemsSm={3} className="gap-4">
          <KPICell IconC={CurrencyDollarIcon} iconBg="bg-pink-100" iconColor="text-[#FF3489]" label="Total Ad Spend" value="$13.7K" sub="11 active campaigns in view" help />
          <KPICell IconC={ChartBarIcon} iconBg="bg-teal-100" iconColor="text-teal-600" label="Marketing Pipeline" value="$0" sub="0 attributed deals" help />
          <KPICell IconC={UsersIcon} iconBg="bg-purple-100" iconColor="text-purple-600" label="Cost Per Acquisition" value="N/A" sub="Awaiting first conversion" help />
        </Grid>
        <div className="mt-4 p-3 bg-white/60 rounded-xl flex items-start gap-2">
          <ClockIcon className="h-5 w-5 text-orange-500 mt-0.5 flex-shrink-0" />
          <Text className="text-sm text-orange-700">
            Campaigns active with <strong className="font-semibold">$13.7K</strong> in spend. No attributed deals yet. Verify UTM tracking is firing on destination URLs.
          </Text>
        </div>
      </Card>

      <Grid numItemsLg={2} className="gap-4">
        {/* Funnel by source — DISABLED until HubSpot attribution + Meta UTMs land */}
        <Card className="bg-blue-50/80 backdrop-blur-2xl rounded-3xl border border-blue-200 shadow-xl">
          <Flex alignItems="center" className="gap-3 mb-5">
            <div className="p-2.5 bg-white/70 rounded-xl shadow-sm">
              <ChartBarIcon className="h-6 w-6 text-blue-500" />
            </div>
            <div>
              <Text className="text-xl font-bold text-gray-900">Funnel by source</Text>
              <Text className="text-sm text-gray-500">Lead → MQL → SQL → Opp · awaiting data</Text>
            </div>
          </Flex>
          <div className="bg-white/70 rounded-2xl p-5">
            <Text className="text-sm text-blue-700 leading-relaxed">
              <strong className="font-semibold">Not yet wired.</strong> Per-channel funnel conversion requires UTM tagging in HubSpot (Meta UTMs are missing — Haydon to fix) and a HubSpot lifecycle stage by analytics_source view. The current <code>v_marketing_roi_unified</code> view returns 0 attributed deals for Meta and a handful for Google. Wiring estimate: 2 days once Meta UTMs are live.
            </Text>
          </div>
        </Card>

        {/* Top campaigns — REAL spend numbers from BigQuery, attribution honestly marked */}
        <Card className="bg-white/80 backdrop-blur-2xl rounded-3xl border border-white/20 shadow-xl">
          <Flex alignItems="center" className="gap-3 mb-5">
            <div className="p-2.5 bg-white/70 rounded-xl shadow-sm">
              <TrophyIcon className="h-6 w-6 text-amber-500" />
            </div>
            <div>
              <Text className="text-xl font-bold text-gray-900">Top campaigns by spend</Text>
              <Text className="text-sm text-gray-500">Real BQ data · attribution column awaiting HubSpot wiring</Text>
            </div>
          </Flex>
          <CampaignRow name="SW: 3PL Warehouse" channel="Google" meta="2,191 impressions · 61 clicks · CPC $43.37" value="$2,645" valueSub="spend" />
          <CampaignRow name="SW: PMax" channel="Google" meta="14,814 impressions · 658 clicks · CPC $3.14" value="$2,064" valueSub="spend" />
          <CampaignRow name="SW: Conquest" channel="Google" meta="15,650 impressions · 84 clicks · CPC $22.43" value="$1,884" valueSub="spend" />
          <CampaignRow name="SW: 3PL Warehouse | AI Max" channel="Google" meta="3,784 impressions · 93 clicks · CPC $16.85" value="$1,567" valueSub="spend" />
          <CampaignRow name="SW: LaL" channel="Meta" meta="23,631 impressions · 558 clicks · CPC $2.46" value="$1,372" valueSub="spend" />
          <CampaignRow name="SW: General" channel="Google" meta="771 impressions · 28 clicks · CPC $47.51" value="$1,330" valueSub="spend" />
          <CampaignRow name="SW: Remarketing" channel="Meta" meta="15,840 impressions · 174 clicks · CPC $6.36" value="$1,107" valueSub="spend" last />
        </Card>
      </Grid>
    </div>
  );
}

// =========================================================================
// SECTION: VOICE
// =========================================================================
function SectionVoice() {
  return (
    <div className="space-y-4">
      <SectionHeader
        Icon={ChatBubbleLeftRightIcon}
        iconBg="bg-pink-100"
        iconColor="text-[#FF3489]"
        title="Customer voice"
        sub="What the market is telling us · from 476 indexed Fathom calls"
      />
      <Grid numItemsLg={2} className="gap-4">
        <Card className="bg-blue-50/80 backdrop-blur-2xl rounded-3xl border border-blue-200 shadow-xl">
          <Flex justifyContent="between" alignItems="center" className="mb-5">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-white/70 rounded-xl shadow-sm">
                <ChatBubbleLeftRightIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <Text className="text-xl font-bold text-gray-900">Recent customer quotes</Text>
                <Text className="text-sm text-gray-500">From Fathom calls · awaiting LLM extraction</Text>
              </div>
            </div>
            <Badge color="blue" size="lg">Not yet wired</Badge>
          </Flex>
          <div className="bg-white/70 rounded-2xl p-5">
            <Text className="text-sm text-blue-700 leading-relaxed">
              <strong className="font-semibold">Needs an LLM extraction pass over Fathom call transcripts.</strong> Octup has 476 indexed calls in Fathom with full summaries and action items. To surface this card, we'd run a weekly job that selects the past 7 days of calls, asks an LLM to pull objection / win quotes per company, and writes them to a <code>customer_voice</code> BigQuery table. Wiring estimate: 1-2 days plus prompt tuning.
            </Text>
          </div>
        </Card>

        <Card className="bg-blue-50/80 backdrop-blur-2xl rounded-3xl border border-blue-200 shadow-xl">
          <Flex justifyContent="between" alignItems="center" className="mb-5">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-white/70 rounded-xl shadow-sm">
                <CheckCircleIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <Text className="text-xl font-bold text-gray-900">Won/lost themes · last 30 days</Text>
                <Text className="text-sm text-gray-500">Aggregated reasons · awaiting data</Text>
              </div>
            </div>
            <Badge color="blue" size="lg">Not yet wired</Badge>
          </Flex>
          <div className="bg-white/70 rounded-2xl p-5">
            <Text className="text-sm text-blue-700 leading-relaxed">
              <strong className="font-semibold">Needs close-reason capture in HubSpot.</strong> The why-we-win / why-we-lose breakdown depends on either a structured close-reason field on every closed deal or an LLM pass over deal notes. Currently HubSpot has free-text "reason for closed" on some deals but it's not consistently filled. Two paths: (1) make it a required field, or (2) LLM-extract themes from existing notes weekly. Wiring estimate: 1 day once a path is chosen.
            </Text>
          </div>
        </Card>
      </Grid>
    </div>
  );
}

// =========================================================================
// SHARED SMALL COMPONENTS
// =========================================================================

function SectionHeader({ Icon, iconBg, iconColor, title, sub }) {
  return (
    <div className="flex items-center gap-3">
      <div className={`p-2.5 ${iconBg} rounded-xl`}>
        <Icon className={`h-5 w-5 ${iconColor}`} />
      </div>
      <div>
        <Text className="text-base font-bold text-gray-900">{title}</Text>
        <Text className="text-xs text-gray-500 mt-0.5">{sub}</Text>
      </div>
    </div>
  );
}

function WeekCell({ value, label, delta, deltaUp, deltaDown }) {
  const deltaCls = deltaUp ? 'text-emerald-600' : deltaDown ? 'text-red-600' : 'text-gray-500';
  return (
    <div className="bg-white/70 rounded-2xl p-5 shadow-sm">
      <Text className="text-3xl font-bold text-gray-900 font-mono">{value}</Text>
      <Text className="text-xs text-gray-500 mt-1">{label}</Text>
      <Text className={`text-xs font-mono mt-2 ${deltaCls}`}>{delta}</Text>
    </div>
  );
}

function DealRow({ name, meta, value, valueSub, tag, tagColor = 'gray', last = false }) {
  return (
    <div className={`flex justify-between items-center py-2.5 ${last ? '' : 'border-b border-gray-100'}`}>
      <div className="min-w-0">
        <Text className="font-semibold text-gray-900 text-sm">{name}</Text>
        <Text className="text-xs text-gray-500 mt-0.5">{meta}</Text>
      </div>
      {tag ? (
        <Badge color={tagColor} size="xs">{tag}</Badge>
      ) : (
        <div className="text-right ml-3 flex-shrink-0">
          <Text className="font-bold font-mono text-gray-900 text-sm">{value}</Text>
          <Text className="text-[10px] text-gray-400 uppercase tracking-wide mt-0.5">{valueSub}</Text>
        </div>
      )}
    </div>
  );
}

function LegendRow({ color, label, value }) {
  return (
    <div className="flex justify-between text-sm">
      <div className="flex items-center gap-2 text-gray-700">
        <span className="w-2.5 h-2.5 rounded-sm" style={{ background: color }} />
        {label}
      </div>
      <span className="font-mono font-semibold text-gray-900">{value}</span>
    </div>
  );
}

function KPICell({ IconC, iconBg, iconColor, label, value, sub, help }) {
  return (
    <div className="bg-white/70 rounded-2xl p-5 shadow-sm">
      <Flex alignItems="start" className="gap-3 mb-3">
        <div className={`p-2 ${iconBg} rounded-lg`}>
          <IconC className={`h-5 w-5 ${iconColor}`} />
        </div>
        <div className="flex-1 flex justify-between items-start">
          <Text className="text-xs text-gray-500 uppercase tracking-wide font-semibold">{label}</Text>
          {help && (
            <div className="w-5 h-5 rounded-full bg-teal-500 text-white text-xs font-semibold flex items-center justify-center cursor-help">?</div>
          )}
        </div>
      </Flex>
      <Text className="text-3xl font-bold text-gray-900 font-mono">{value}</Text>
      <Text className="text-xs text-gray-500 mt-1">{sub}</Text>
    </div>
  );
}

function Sparkline({ values }) {
  return (
    <div className="flex items-end gap-1 h-12 mt-3">
      {values.map((v, i) => (
        <div
          key={i}
          className="flex-1 rounded-sm"
          style={{ height: `${v}%`, background: '#00CBC0', opacity: i === values.length - 1 ? 1 : 0.55 }}
        />
      ))}
    </div>
  );
}

function FunnelRow({ label, values, color }) {
  return (
    <div>
      <Flex justifyContent="between" className="mb-1.5">
        <Text className="text-xs font-semibold text-gray-900">{label}</Text>
        <Text className="text-xs font-mono text-gray-500">{values.map(v => v < 1 ? '·' : Math.round(v)).join(' → ')}</Text>
      </Flex>
      <div className="flex gap-0.5">
        {values.map((v, i) => (
          <div
            key={i}
            className={`h-3 ${i === 0 ? 'rounded-l' : ''} ${i === values.length - 1 ? 'rounded-r' : ''}`}
            style={{
              flex: Math.max(v, 0.1),
              background: color,
              opacity: [1, 0.75, 0.55, 0.35][i] || 0.35,
            }}
          />
        ))}
      </div>
    </div>
  );
}

function CampaignRow({ name, channel, meta, value, valueSub = 'attr. pipeline', valueSubRed = false, last = false }) {
  return (
    <div className={`flex justify-between items-center py-2.5 ${last ? '' : 'border-b border-gray-100'}`}>
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <Text className="font-semibold text-gray-900 text-sm">{name}</Text>
          <Badge color="gray" size="xs">{channel}</Badge>
        </div>
        <Text className="text-xs text-gray-500 mt-0.5">{meta}</Text>
      </div>
      <div className="text-right ml-3 flex-shrink-0">
        <Text className="font-bold font-mono text-gray-900 text-sm">{value}</Text>
        <Text className={`text-[10px] uppercase tracking-wide mt-0.5 ${valueSubRed ? 'text-red-600' : 'text-gray-400'}`}>{valueSub}</Text>
      </div>
    </div>
  );
}

function Quote({ text, meta, last = false }) {
  return (
    <div className={`py-3 ${last ? '' : 'border-b border-gray-100'}`}>
      <Text className="text-sm italic text-gray-900 leading-relaxed mb-1.5">"{text}"</Text>
      <Text className="text-xs text-gray-500 font-mono">{meta}</Text>
    </div>
  );
}

function ThemeRow({ name, meta, last = false }) {
  return (
    <div className={`py-2 ${last ? '' : 'border-b border-gray-100'}`}>
      <Text className="font-semibold text-gray-900 text-sm">{name}</Text>
      <Text className="text-xs text-gray-500 mt-0.5">{meta}</Text>
    </div>
  );
}
